import { useCallback, useEffect, useRef, useState } from 'react';

import { ReloadOutlined } from '@ant-design/icons';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { getToken } from 'services/http';
import { FRONTEND_RELEASE_PROJECT, getFrontendReleaseTime } from 'services/api/frontendReleaseTime';

const CHECK_INTERVAL = 60 * 1000;

function normalizeTime(value) {
  return value || '';
}

function parseDateTime(value) {
  if (!value) return null;
  const time = new Date(String(value).replace(' ', 'T')).getTime();
  return Number.isNaN(time) ? null : time;
}

function shouldPrompt(data) {
  const releaseTime = normalizeTime(data?.release_time);
  const refreshTime = parseDateTime(data?.refresh_time);
  return Boolean(releaseTime) && Boolean(refreshTime) && Date.now() > refreshTime;
}

export default function FrontendReleaseCheckUpdates() {
  const [open, setOpen] = useState(false);
  const releaseTimeRef = useRef('');
  const nextReleaseTimeRef = useRef('');
  const promptedReleaseTimeRef = useRef('');
  const checkingRef = useRef(false);
  const initializedRef = useRef(false);

  const checkFrontendReleaseTime = useCallback(async () => {
    if (!getToken() || checkingRef.current) return;

    checkingRef.current = true;
    try {
      const response = await getFrontendReleaseTime({ project: FRONTEND_RELEASE_PROJECT });
      const data = Number(response?.code) === 0 ? response.data : null;
      if (!data) return;

      const nextReleaseTime = normalizeTime(data.release_time);

      if (!initializedRef.current) {
        initializedRef.current = true;
        releaseTimeRef.current = nextReleaseTime;
        return;
      }

      if (nextReleaseTime !== releaseTimeRef.current && nextReleaseTime !== promptedReleaseTimeRef.current && shouldPrompt(data)) {
        promptedReleaseTimeRef.current = nextReleaseTime;
        nextReleaseTimeRef.current = nextReleaseTime;
        setOpen(true);
        return;
      }

      if (nextReleaseTime === releaseTimeRef.current) {
        promptedReleaseTimeRef.current = '';
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('frontend release check failed', error);
      }
    } finally {
      checkingRef.current = false;
    }
  }, []);

  useEffect(() => {
    checkFrontendReleaseTime();
    const timer = window.setInterval(checkFrontendReleaseTime, CHECK_INTERVAL);
    const handleVisibilityChange = () => {
      if (!document.hidden) checkFrontendReleaseTime();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkFrontendReleaseTime]);

  const handleRefresh = () => {
    releaseTimeRef.current = nextReleaseTimeRef.current;
    window.location.reload();
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth aria-labelledby="frontend-release-dialog-title">
      <DialogTitle id="frontend-release-dialog-title">版本更新提示</DialogTitle>
      <DialogContent>
        <DialogContentText>检测到新版本，请刷新页面以获取最新内容。</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setOpen(false)}>稍后</Button>
        <Button variant="contained" startIcon={<ReloadOutlined />} onClick={handleRefresh} autoFocus>
          立即刷新
        </Button>
      </DialogActions>
    </Dialog>
  );
}
